import { useEffect, useRef, useState, type FormEvent } from "react";
import { SPOT_SSE_EVENT, type SpotKeyView } from "@auto-pools/core";
import { api, ApiError } from "../../api/client";
import { useResource } from "../../api/useResource";
import { useViewer } from "../../auth/authContext";
import { formatAgo, formatWhen } from "../../lib/format";
import {
  permissionsSeenText,
  spotKeyStateText,
  toleratedPermissionsText,
  toPem,
} from "./spotKeyPresentation";

type DialogAction = "generate" | "activate" | null;

/**
 * The key's state changes on the server without any action here (the hourly check finds it
 * revoked, a known expiry passes): re-read on the `spot` event and at least once a minute.
 */
const KEY_REFRESH_MS = 60_000;

/** A refused password keeps the dialog open for another try (design 53, plan Phase 1). */
function keyErrorText(reason: unknown, fallback: string): string {
  if (reason instanceof ApiError && reason.code === "AUTH_FAILED")
    return "The password was not accepted. Try again.";
  return reason instanceof ApiError ? reason.message : fallback;
}

/** A clipboard write that never throws into the render path (private window, denied permission). */
async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** A plain, non-null `text` prop keeps every copy call site free of a non-null assertion. */
function CopyButton({
  text,
  copied,
  onCopied,
  children,
}: {
  text: string;
  copied: boolean;
  onCopied: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      className="button ghost small"
      onClick={() => {
        void copy(text).then((ok) => {
          if (ok) onCopied();
        });
      }}
    >
      {copied ? "Copied" : children}
    </button>
  );
}

/**
 * The Bybit key block (design `53`): one card whose body is entirely decided by `SpotKeyView`'s
 * own state/reason — never a client-side guess at which of the twelve cards to show.
 */
export function SpotKeyBlock() {
  const key = useResource<SpotKeyView>(
    "spot-key",
    (signal) => api("/spot/key", { signal, cache: "no-store" }),
    KEY_REFRESH_MS,
  );
  const reloadKey = key.reload;
  const viewer = useViewer();
  useEffect(() => {
    const onServerEvent = (event: Event) => {
      if ((event as CustomEvent<unknown>).detail === SPOT_SSE_EVENT) reloadKey();
    };
    window.addEventListener("auto-pools:server-event", onServerEvent);
    return () => {
      window.removeEventListener("auto-pools:server-event", onServerEvent);
    };
  }, [reloadKey]);
  const [dialog, setDialog] = useState<DialogAction>(null);
  const [password, setPassword] = useState("");
  const [apiKeyId, setApiKeyId] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<"public-key" | "ip" | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  function closeDialog() {
    if (pending) return;
    setDialog(null);
    setPassword("");
    setApiKeyId("");
    setError(null);
  }

  function announceCopy(which: "public-key" | "ip") {
    clearTimeout(copyTimer.current);
    setCopied(which);
    copyTimer.current = setTimeout(() => {
      setCopied(null);
    }, 2000);
  }

  async function submitGenerate(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await api<SpotKeyView>("/spot/key/generate", {
        method: "POST",
        body: { password },
        sensitive: true,
        passwordReauthentication: true,
      });
      key.reload();
      setDialog(null);
      setPassword("");
    } catch (reason) {
      setError(keyErrorText(reason, "Could not generate a key pair"));
    } finally {
      setPending(false);
    }
  }

  async function submitActivate(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await api<SpotKeyView>("/spot/key/activate", {
        method: "POST",
        body: { apiKeyId, password },
        sensitive: true,
        passwordReauthentication: true,
      });
      key.reload();
      setDialog(null);
      setPassword("");
      setApiKeyId("");
    } catch (reason) {
      setError(keyErrorText(reason, "Bybit did not accept this key"));
    } finally {
      setPending(false);
    }
  }

  async function checkNow() {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await api<SpotKeyView>("/spot/key/check", { method: "POST" });
      key.reload();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Could not reach Bybit");
    } finally {
      setPending(false);
    }
  }

  const view = key.data;
  const text = view ? spotKeyStateText(view.state, view.reason, view.active !== null) : null;
  const permissionsText = view?.permissions ? permissionsSeenText(view.permissions) : null;
  const attemptPermissions = view?.pending?.lastAttempt?.permissions;
  const attemptPermissionsText = attemptPermissions
    ? permissionsSeenText(attemptPermissions)
    : null;
  // A refusal's list shows in the banner; an accepted check's list is the tolerated extras.
  const lastCheck = view?.active?.lastCheck;
  const toleratedText =
    lastCheck?.permissions && lastCheck.state !== "refused-permissions"
      ? toleratedPermissionsText(lastCheck.permissions)
      : null;

  return (
    <section className="panel settings-card spot-key-block">
      <h2>Bybit key · spot leg</h2>
      {key.loading && <p className="muted">Checking the key…</p>}
      {key.error && <p className="inline-alert error">{key.error.message}</p>}
      {view && text && (
        <>
          <div className={`spot-key-banner ${text.tone}`}>
            <i
              className={`status-dot ${text.tone === "good" ? "good" : text.tone === "bad" ? "bad" : text.tone === "warn" ? "warn" : ""}`}
            />
            <div>
              <b>{text.title}</b>
              <p>{text.description}</p>
              {permissionsText && <p className="spot-key-permissions">{permissionsText}</p>}
            </div>
          </div>

          {view.vault === "locked" && (
            <p className="inline-alert warning">
              Vault locked: a new key cannot be generated and the stored key cannot sign until the
              vault is unlocked.
            </p>
          )}

          {view.active && (
            <div className="spot-key-detail-rows">
              <div className="spot-key-detail-row">
                <span>Key</span>
                <code className="num">{view.active.apiKeyIdMasked}</code>
              </div>
              <div className="spot-key-detail-row">
                <span>Subaccount</span>
                <span className="num">
                  {view.active.userId} · main account {view.active.parentUid}
                </span>
              </div>
              <div className="spot-key-detail-row">
                <span>Bound to the server IP</span>
                <span className="num">
                  {view.active.bound ? "yes" : "no — expires after 90 days"}
                </span>
              </div>
              <div className="spot-key-detail-row">
                <span>Expires</span>
                <span className="num">
                  {view.active.expiresAt === null
                    ? "Bybit reports no expiry"
                    : `${formatWhen(view.active.expiresAt)}${
                        view.active.daysLeft === null
                          ? ""
                          : ` · ${String(view.active.daysLeft)} days left`
                      }`}
                </span>
              </div>
              <div className="spot-key-detail-row">
                <span>Added</span>
                <span className="num">{formatAgo(view.active.addedAt)}</span>
              </div>
              <div className="spot-key-detail-row">
                <span>Last check</span>
                <span className="num">
                  {view.active.lastCheck === null
                    ? "never checked"
                    : formatAgo(view.active.lastCheck.checkedAt)}
                </span>
              </div>
              <div className="spot-key-detail-row">
                <span>Last accepted</span>
                <span className="num">
                  {view.active.lastOkAt === null ? "never" : formatAgo(view.active.lastOkAt)}
                </span>
              </div>
            </div>
          )}
          {toleratedText && (
            <p className="inline-alert warning spot-key-tolerated">{toleratedText}</p>
          )}

          {/* A replacement keeps the active key beside the new pair (server `keys.ts`), so the
              pair's steps show whenever one is pending, with or without an active key. */}
          {view.pending && (
            <div className="spot-key-pending">
              {view.active && (
                <p className="spot-key-replace-note">
                  Replacing: the old key keeps working until the new one answers.
                </p>
              )}
              <ol className="spot-key-steps">
                <li className="spot-key-step done">Pair generated</li>
                <li className="spot-key-step active">
                  <p>
                    Copy the public key and add it as a Self-generated API key on Bybit — tick only
                    SPOT → Trade (Bybit may add Unified Trading · Trade itself; that is accepted),
                    on a subaccount set to no main-account trading. Paste the whole block, including
                    the BEGIN and END lines.
                  </p>
                  <div className="spot-key-public">
                    <code className="num spot-key-public-value">
                      {toPem(view.pending.publicKey)}
                    </code>
                    <CopyButton
                      text={toPem(view.pending.publicKey)}
                      copied={copied === "public-key"}
                      onCopied={() => {
                        announceCopy("public-key");
                      }}
                    >
                      Copy public key
                    </CopyButton>
                  </div>
                  {view.egressIp !== null ? (
                    <div className="spot-key-ip">
                      <span>Bind it to this server IP so it never expires:</span>
                      <code className="num">{view.egressIp}</code>
                      <CopyButton
                        text={view.egressIp}
                        copied={copied === "ip"}
                        onCopied={() => {
                          announceCopy("ip");
                        }}
                      >
                        Copy IP
                      </CopyButton>
                    </div>
                  ) : (
                    <p className="spot-key-ip-unavailable">
                      — · the server egress IP is{" "}
                      {view.egressIpReason === "invalid" ? "not valid" : "not configured"} yet; an
                      unbound key still works for 90 days.
                    </p>
                  )}
                </li>
                <li className="spot-key-step active">
                  <p>Paste the API key ID Bybit gave you:</p>
                  {!viewer && (
                    <button
                      type="button"
                      className="button primary"
                      disabled={view.vault === "locked"}
                      onClick={() => {
                        setDialog("activate");
                      }}
                    >
                      Check and save
                    </button>
                  )}
                </li>
              </ol>
              {view.pending.lastAttempt && (
                <p className="inline-alert warning">
                  Last attempt:{" "}
                  {
                    spotKeyStateText(
                      view.pending.lastAttempt.state,
                      view.pending.lastAttempt.reason,
                      false,
                    ).title
                  }{" "}
                  · {formatAgo(view.pending.lastAttempt.checkedAt)}
                  {attemptPermissionsText && ` · ${attemptPermissionsText}`}
                </p>
              )}
            </div>
          )}

          {!viewer && (
            <div className="spot-key-actions">
              {view.active && (
                <button
                  type="button"
                  className="button ghost"
                  disabled={pending}
                  onClick={() => void checkNow()}
                >
                  {pending ? "Checking…" : "Check now"}
                </button>
              )}
              {view.vault !== "locked" && (
                <button
                  type="button"
                  className="button ghost"
                  disabled={pending}
                  onClick={() => {
                    setDialog("generate");
                  }}
                >
                  {view.active
                    ? "Replace key"
                    : view.pending
                      ? "Start over"
                      : "Generate a key pair"}
                </button>
              )}
            </div>
          )}
          {error && !dialog && (
            <p className="inline-alert error" role="alert">
              {error}
            </p>
          )}
        </>
      )}

      {dialog && !viewer && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDialog();
          }}
        >
          <section
            className="modal action-modal spot-key-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="spot-key-dialog-title"
          >
            <header className="modal-header">
              <h2 id="spot-key-dialog-title">
                {dialog === "generate" ? "Generate a key pair" : "Check and save"}
              </h2>
              <button
                className="icon-button"
                disabled={pending}
                onClick={closeDialog}
                aria-label="Cancel"
              >
                ×
              </button>
            </header>
            {dialog === "generate" ? (
              <form className="stack" onSubmit={(event) => void submitGenerate(event)}>
                {view?.pending && (
                  <p className="inline-alert warning">
                    A pending pair already exists. Generating a new one replaces it — the old public
                    key will no longer match anything pasted into Bybit.
                  </p>
                )}
                <label>
                  Account password
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                    required
                  />
                </label>
                <button className="button primary" disabled={!password || pending}>
                  {pending ? "Generating…" : "Generate"}
                </button>
              </form>
            ) : (
              <form className="stack" onSubmit={(event) => void submitActivate(event)}>
                <label>
                  API key ID
                  <input
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={apiKeyId}
                    onChange={(event) => {
                      setApiKeyId(event.target.value);
                    }}
                    required
                    minLength={1}
                    maxLength={64}
                  />
                </label>
                <label>
                  Account password
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                    required
                  />
                </label>
                <button className="button primary" disabled={!apiKeyId || !password || pending}>
                  {pending ? "Checking…" : "Check and save"}
                </button>
              </form>
            )}
            {error && (
              <p className="inline-alert error" role="alert">
                {error}
              </p>
            )}
          </section>
        </div>
      )}
    </section>
  );
}
